import { Injectable } from '@angular/core';
import { E_EChartsWorkerAction } from '../../enums/echarts-worker.enum';

@Injectable({ providedIn: 'root' })
export class WorkerPoolService {
  private nodes: C_WorkerNode[] = [];
  private taskToNodeMap = new Map<string, C_NodeWrapper>();
  /**
   * We reserve threads to maintain system fluidity and avoid main-thread jank:
   * - 1 thread: Reserved exclusively for the Display Socket (critical path).
   * - 1 thread: Safety buffer for browser overhead, UI rendering, Garbage Collection, 
   *   and unexpected spikes. 
   * 
   * Keeping these threads free prevents thread contention, which is a common 
   * cause of browser freezes and dropped frames in heavy Angular/ECharts apps.
   */
  private readonly MAX_TOTAL_WORKERS = Math.max(2, (navigator.hardwareConcurrency || 4) - 2);
  private isInitialized = false;

  private factoryMap = new Map<E_WorkerType, () => Worker>([
    [E_WorkerType.CHART, () => new Worker(new URL('./charts/charts.worker', import.meta.url))],
    [E_WorkerType.SOCKET, () => new Worker(new URL('./websocket/socket-processor.worker.ts', import.meta.url))]
  ]);

  public initPool() {
    if (this.isInitialized) return;

    // 1. Crear el worker de SOCKET (siempre tiene prioridad)
    this.nodes.push(new C_WorkerNode({
      worker: this.factoryMap.get(E_WorkerType.SOCKET)!(),
      type: E_WorkerType.SOCKET
    }));

    // 2. Rellenar el resto con CHART
    for (let i = 1; i < this.MAX_TOTAL_WORKERS; i++) {
      this.nodes.push(new C_WorkerNode({
        worker: this.factoryMap.get(E_WorkerType.CHART)!(),
        type: E_WorkerType.CHART
      }));
    }
    this.isInitialized = true;
  }

  public getWorker(config: I_WorkerConfig): Worker {
    if (!this.isInitialized) this.initPool();
    const { taskId, type, weight = 1, isExclusive = false } = config;

    // Si es exclusivo (SOCKET), buscamos el nodo de tipo SOCKET que ya existe en el pool
    if (isExclusive) {
      const dedicatedNode = this.nodes.find(n => n.type === type);
      if (!dedicatedNode) throw new Error("No hay slot de socket disponible");

      dedicatedNode.assign({ id: taskId, weight: Infinity });
      this.taskToNodeMap.set(taskId, new C_NodeWrapper({ node: dedicatedNode, weight: Infinity }));
      return dedicatedNode.worker;
    }

    // Estrategia estándar para gráficas: Buscar el más libre dentro de los existentes
    const targetNode = this.nodes
      .filter(n => n.type === type)
      .reduce((prev, curr) => (prev.totalLoad <= curr.totalLoad ? prev : curr));

    targetNode.assign({ id: taskId, weight });
    this.taskToNodeMap.set(taskId, new C_NodeWrapper({ node: targetNode, weight }));
    return targetNode.worker;
  }

  public releaseWorker(taskId: string) {
    const entry = this.taskToNodeMap.get(taskId);
    if (!entry) return;

    entry.node.release(taskId);

    // NO destruimos el worker si es el del socket, solo limpiamos su carga
    if (entry.weight !== Infinity) {
      entry.node.worker.postMessage({ type: E_EChartsWorkerAction.DISPOSE, chartId: taskId });
    }

    this.taskToNodeMap.delete(taskId);
  }

  public destroyPool() {
    this.nodes.forEach(n => n.worker.terminate());
    this.nodes = [];
    this.taskToNodeMap.clear();
    this.isInitialized = false;
  }
}

class C_WorkerNode {
  public totalLoad = 0;
  public worker: Worker;
  public type: E_WorkerType;
  private tasks = new Map<string, number>();

  // Constructor inyectado con objeto de configuración
  constructor(config: I_WorkerNodeConfig) {
    this.worker = config.worker;
    this.type = config.type;
  }

  assign({ id, weight }: { id: string; weight: number }) {
    this.tasks.set(id, weight);
    this.totalLoad += weight;
  }

  release(id: string): void {
    const weight = this.tasks.get(id);
    if (weight !== undefined) {
      this.totalLoad -= weight;
      this.tasks.delete(id);
    }
  }
}

class C_NodeWrapper {
  public node: C_WorkerNode;
  public weight: number;

  // Constructor inyectado con objeto de configuración
  constructor(config: I_NodeWrapperConfig) {
    this.node = config.node;
    this.weight = config.weight;
  }
}




export interface I_Node {
  node: C_WorkerNode;
  weight: number;
}


export interface I_WorkerConfig {
  taskId: string;
  type: E_WorkerType;
  weight?: number;
  isExclusive?: boolean;
}

export enum E_WorkerType {
  CHART = 'CHART',
  SOCKET = 'SOCKET'
}


export interface I_WorkerNodeConfig {
  worker: Worker;
  type: E_WorkerType;
}

export interface I_NodeWrapperConfig {
  node: C_WorkerNode;
  weight: number;
}